import {
  AccountInfo,
  AuthError,
  BrowserAuthError,
  Configuration,
  EndSessionRequest,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest
} from '@azure/msal-browser';
import * as jwt from 'jsonwebtoken';
import moment from 'moment';
import { logService } from 'npo-common';

export class AuthenticationService {
  private msalInstance: PublicClientApplication;
  private msalToken!: string;
  private msalAccount!: AccountInfo | null;
  private isInitialized: boolean = false; // Track initialization state

  private msalLoginRequest: RedirectRequest = {
    scopes: [process.env.REACT_APP_AAD_CLIENT_ID + '/.default']
  };

  private msalSilentRequest: SilentRequest = {
    scopes: [process.env.REACT_APP_AAD_CLIENT_ID + '/.default'],
    forceRefresh: false
  };

  constructor() {
    const msalConfig: Configuration = {
      auth: {
        clientId: process.env.REACT_APP_AAD_CLIENT_ID ?? '',
        redirectUri: window.location.origin,
        authority: `${process.env.REACT_APP_AAD_LOGIN_INSTANCE}common`,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: this.isEdgeIE()
      },
      // Simplified telemetry configuration
      telemetry: {
        application: {
          appName: "adm-support-web",
          appVersion: "1.0.0"
        }
      },
      // Correct system configuration for v4.20
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) return;
            switch (level) {
              case LogLevel.Error:
                logService.logError("MSAL Error: " + message);
                break;
              case LogLevel.Warning:
                logService.logInfo("MSAL Warning: " + message);
                break;
              case LogLevel.Info:
                logService.logInfo("MSAL Info: " + message);
                break;
              case LogLevel.Verbose:
                // Only log verbose in development
                if (process.env.NODE_ENV === 'development') {
                  console.log("MSAL Verbose: " + message);
                }
                break;
            }
          },
          piiLoggingEnabled: false,
          logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Verbose : LogLevel.Warning
        }
      }
    };
    this.msalInstance = new PublicClientApplication(msalConfig);
    // Initialize MSAL immediately
    this.initializeMsal();
  }

  // Better browser detection for Edge/IE
  private isEdgeIE(): boolean {
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf("MSIE ") > -1;
    const msie11 = ua.indexOf("Trident/") > -1;
    const edge = ua.indexOf("Edg/") > -1;
    return msie || msie11 || edge;
  }

  // Proper initialization with await
  private async initializeMsal(): Promise<void> {
    try {
      // Initialize MSAL before any other operations
      await this.msalInstance.initialize();
      this.isInitialized = true;
      
      // Handle any pending redirect promise after initialization
      const tokenResponse = await this.msalInstance.handleRedirectPromise();
      if (tokenResponse) {
        this.msalToken = tokenResponse.accessToken;
        this.msalAccount = tokenResponse.account;
      }
      const accounts = this.msalInstance.getAllAccounts();

      if (accounts.length > 0 && !this.msalAccount) {
        this.msalAccount = accounts[0];
      }
    } catch (error) {
      logService.logError("MSAL initialization error: ", error as any);
      this.isInitialized = false;
    }
  }

  // Helper method to ensure initialization before API calls
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initializeMsal();
    }
  }

  public async login(): Promise<void> {
    try {
      await this.ensureInitialized(); // Ensure MSAL is initialized
      await this.msalInstance.loginRedirect(this.msalLoginRequest);
    } catch (error) {
      logService.logError("Login error: ", error as any);
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.ensureInitialized(); // Ensure MSAL is initialized
      
      let account: AccountInfo | undefined;
      if (this.msalAccount) {
        account = this.msalAccount;
      }

      const logoutRequest: EndSessionRequest = { 
        account
      };

      await this.msalInstance.logoutRedirect(logoutRequest);
    } catch (error: any) {
      logService.logError("Logout error: ", error);
      throw error;
    }
  }

  public async handleLoginPromise(): Promise<boolean> {
    try {
      await this.ensureInitialized(); // Ensure MSAL is initialized
      
      // If there is a cached login, use it
      if (this.msalAccount) {
        return Promise.resolve(true);
      }

      const tokenResponse = await this.msalInstance.handleRedirectPromise();
      if (tokenResponse !== null) {
        this.msalToken = tokenResponse.accessToken;
        this.msalAccount = tokenResponse.account;
        return true;
      }
      return false;
    } catch (error) {
      logService.logError("Handle login promise error: ", error as any);
      return false;
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      await this.ensureInitialized(); // Ensure MSAL is initialized
      const token = await this.getToken();
      return token !== null;
    } catch (error) {
      logService.logError("Authentication check error: ", error as any);
      return false;
    }
  }

  public getTokenExpirationInMinutes(token: string): number {
    if (!token) {
      return moment.duration(0).minutes();
    }
    
    try {
      const decodedToken = jwt.decode(token) as { [key: string]: any };
      if (!decodedToken || !decodedToken.exp) {
        return moment.duration(0).minutes();
      }
      
      const expirationDate = new Date(decodedToken.exp * 1000);
      return moment.duration(moment(expirationDate).diff(new Date())).minutes();
    } catch (error) {
      logService.logError("Token decode error: ", error as any);
      return moment.duration(0).minutes();
    }
  }

  public get accountInfo(): AccountInfo | null {
    return this.msalAccount;
  }

  public get cachedToken(): string {
    return this.msalToken;
  }

  public async getToken(): Promise<string | null> {
    try {
      await this.ensureInitialized(); // Ensure MSAL is initialized
      
      // Ensure we have an account for silent token acquisition
      if (!this.msalAccount) {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalAccount = accounts[0];
        } else {
          // No accounts available, need to login
          return null;
        }
      }

      this.msalSilentRequest.account = this.msalAccount;

      const response = await this.msalInstance.acquireTokenSilent(this.msalSilentRequest);
      this.msalToken = response.accessToken;
      this.msalAccount = response.account;

      return response.accessToken;
    } catch (e) {
      // Enhanced error handling in v4
      if (e instanceof InteractionRequiredAuthError) {
        logService.logError("Interaction required: " + e.message, e);
        // Don't automatically redirect, let the calling code decide
        throw e;
      } else if (e instanceof BrowserAuthError) {
        logService.logError("Browser auth error: " + e.message, e);
      } else if (e instanceof AuthError) {
        logService.logError("Auth error: " + e.message, e);
      } else {
        logService.logError("Unknown error during token acquisition: ", e as any);
      }

      return null;
    }
  }

  // Simplified helper methods
  public async getAllAccounts(): Promise<AccountInfo[]> {
    await this.ensureInitialized(); // Ensure MSAL is initialized
    return this.msalInstance.getAllAccounts();
  }
}

const authenticationService = new AuthenticationService();
export default authenticationService;
